import { Typography, Alert, AlertTitle } from "@mui/material";
import {
  Card,
  Grid,
  TextField,
  IconButton,
  Button,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, styled, useTheme } from "@mui/system";
import { AuthContext } from "src/contexts/JWTAuthContext";
import { Formik } from "formik";
import { useContext, FC, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { UAParser } from "ua-parser-js";
import { FormattedMessage } from "react-intl";
import { flavor } from "src/settings";
import { loginValidationSchema } from "src/utils/utils";

const FlexBox = styled(Box)(() => ({ display: "flex", alignItems: "center" }));

const JustifyBox = styled(FlexBox)(() => ({ justifyContent: "center" }));

const ContentBox = styled(Box)(() => ({
  height: "100%",
  padding: "32px",
  position: "relative",
}));

interface LoginProps {
  lang: string;
  registrationEnabled: boolean;
  emailingEnabled: boolean;
}

const JwtAdminLoginForm: FC<LoginProps> = ({
  lang,
  registrationEnabled,
  emailingEnabled,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const errorMessageRef = useRef("");
  const loadingRef = useRef(false);
  const { login } = useContext(AuthContext);

  const JWTRoot = styled(JustifyBox)(() => ({
    background: theme.palette.common.black,
    minHeight: "100% !important",
    "& .card": {
      maxWidth: 800,
      minHeight: 400,
      margin: "1rem",
      display: "flex",
      borderRadius: 12,
      alignItems: "center",
    },
  }));

  const initialValues = {
    username: "",
    password: "",
    showPword: "password",
  };

  const handleFormSubmit = async (values) => {
    loadingRef.current = true;
    const browserInfo = new UAParser();
    const response = await login(
      values.username,
      values.password,
      browserInfo.getBrowser().name + " " + browserInfo.getBrowser().version
    );
    loadingRef.current = false;
    if (response.success) {
      navigate("/");
    } else {
      errorMessageRef.current = response.message;
    }
  };

  return (
    <JWTRoot>
      <Card className="card">
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="stretch"
        >
          <Grid>
            <Grid container>
              <Grid size={{sm:6, xs:12}}>
                <JustifyBox p={4} height="100%" sx={{ minWidth: 320 }}>
                  <img src={"/" + flavor + "/logo.png"} width="100%"  alt="" />
                </JustifyBox>
              </Grid>

              <Grid size={{sm:6, xs:12}}>
              
                <ContentBox>
                  <Formik
                    onSubmit={handleFormSubmit}
                    initialValues={initialValues}
                    validationSchema={loginValidationSchema}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      handleSubmit,
                      setFieldValue,
                    }) => (
                      <form onSubmit={handleSubmit}>
                        <div>
                      <Typography component="span" variant="h2">
                        <FormattedMessage id="signInPrompt" />
                      </Typography>
                    </div>
                    <br />
                        <TextField
                          fullWidth
                          required
                          type="text"
                          name="username"
                          label={<FormattedMessage id="emailOrIdentifier" />}
                          variant="outlined"
                          onBlur={handleBlur}
                          value={values.username}
                          onChange={(e) => {
                            handleChange(e);
                            errorMessageRef.current = "";
                          }}
                          helperText={
                            touched.username &&
                            errors.username && (
                              <FormattedMessage id={String(errors.username)} />
                            )
                          }
                          error={Boolean(errors.username && touched.username)}
                          sx={{ mb: 3 }}
                        />

                        <TextField
                          fullWidth
                          required
                          name="password"
                          type={values.showPword}
                          label={<FormattedMessage id="password" />}
                          variant="outlined"
                          onBlur={handleBlur}
                          value={values.password}
                          onChange={(e) => {
                            handleChange(e);
                            errorMessageRef.current = "";
                          }}
                          helperText={
                            touched.password &&
                            errors.password && (
                              <FormattedMessage id={String(errors.password)} />
                            )
                          }
                          error={Boolean(errors.password && touched.password)}
                          sx={{ mb: 1.5 }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => {
                                    setFieldValue(
                                      "showPword",
                                      values.showPword === "password"
                                        ? "text"
                                        : "password"
                                    );
                                  }}
                                  edge="end"
                                >
                                  {values.showPword === "text" ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <FlexBox justifyContent="space-between" sx={{ mb: 3 }}>
                          <Button
                            type="submit"
                            color="primary"
                            loading={loadingRef.current}
                            variant="contained"
                            sx={{ my: 2, mr: 2, minWidth: 90 }}
                          >
                            {<FormattedMessage id="requestOTP" />}
                          </Button>

                          {errorMessageRef.current && (
                            <Alert
                              severity="error"
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <AlertTitle sx={{ mt: 0.3 }}>
                                <FormattedMessage
                                  id={errorMessageRef.current}
                                />
                              </AlertTitle>
                            </Alert>
                          )}
                        </FlexBox>

                        {emailingEnabled && (
                          <Typography component="span" variant="subtitle2">
                            <div style={{ textAlign: "right" }}>
                              {registrationEnabled && (
                                <>
                                  {<FormattedMessage id="notYetHaveAccount" />}
                                  <br />
                                  <NavLink
                                    to={"/signup/" + lang}
                                    style={{
                                      fontWeight: 700,
                                      color: theme.palette.text.primary,
                                      marginLeft: 5,
                                    }}
                                  >
                                    {<FormattedMessage id="registerNow" />}
                                  </NavLink>
                                </>
                              )}
                            </div>
                          </Typography>
                        )}
                      </form>
                    )}
                  </Formik>
                </ContentBox>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Card>
    </JWTRoot>
  );
};

export default JwtAdminLoginForm;
